import styled, { css } from 'styled-components'
import { getAutomatedStyleProp } from '../../providers/ComponentsThemeProvider'
import { mediaQueries } from '../../providers/MediaQueryProvider'
import type { Props } from './types'

export const Container = styled.div<{
  $blurOverlay: Props['blurOverlay']
}>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100vw;
  height: 100vh;
  padding: var(--space-400);

  &::after {
    display: block;
    content: '';
    position: absolute;
    height: 50px;
    width: 100%;
    top: 100%;
    background: ${getAutomatedStyleProp('colorBackground', 'var(--bg-primary)')};
  }

  ${mediaQueries.tablet} {
    flex-direction: row;
    padding: var(--space-1200);

    &::after {
      display: none;
    }
  }

  ${({ $blurOverlay }) =>
    $blurOverlay &&
    css`
      backdrop-filter: blur(100px);
    `};
`

export const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  background: var(--color-black-alpha-1500);
`

type BoxProps = {
  size: NonNullable<Props['size']>
  maxWidth: Props['maxWidth']
  $withoutPadding: Props['withoutPadding']
  $disableScrolling: Props['disableScrolling']
  width: Props['width']
}

const getMaxWidth = ({ size, maxWidth, width }: BoxProps) => {
  const maxWidths: Record<BoxProps['size'], string> = {
    small: '480px',
    medium: '600px',
    large: '800px',
    xlarge: '960px',
    fullScreen: 'none',
  }

  if (width) return '100%'

  return maxWidth ? `${maxWidth}px` : maxWidths[size]
}

export const Box = styled.div<BoxProps>`
  flex: ${({ width }) => (width ? '0 1 auto' : '1 0 auto')};
  display: flex;
  flex-direction: column;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;

  padding: var(--space-600) var(--space-500);
  background: ${getAutomatedStyleProp('colorBackground', 'var(--bg-primary)')};
  width: ${({ width }) => width || '100%'};
  max-height: calc(100% - 96px);
  overflow-y: auto;
  overflow-x: hidden;

  border-radius: ${getAutomatedStyleProp('inputCornerRadius', 'var(--radius-brand)')};

  color: ${getAutomatedStyleProp('colorOnBackground', 'var(--text-primary)')};

  ${({ $withoutPadding }) =>
    $withoutPadding &&
    css`
      padding: var(--space-0);
    `};

  ${({ size }) =>
    size === 'fullScreen' &&
    css`
      max-height: none;
      top: 0;
    `};

  ${mediaQueries.tablet} {
    position: relative;
    padding: var(--space-600) var(--space-800);
    bottom: auto;
    left: auto;
    right: auto;
    top: auto;
    overflow: auto;
    max-width: ${getMaxWidth};
    max-height: calc(100vh - 96px);

    ${({ size }) =>
      size === 'fullScreen' &&
      css`
        height: 100%;
      `};

    ${({ $withoutPadding }) =>
      $withoutPadding &&
      css`
        padding: var(--space-0);
      `};

    ${({ $disableScrolling }) =>
      $disableScrolling &&
      css`
        overflow: visible;
      `};
  }
`

export const CloseButton = styled.button`
  position: absolute;
  top: var(--space-100);
  right: var(--space-100);
  z-index: 1;

  display: block;
  padding: var(--space-150);

  color: ${getAutomatedStyleProp('colorOnBackground', 'var(--text-secondary)')};

  &:hover,
  &:focus {
    color: ${getAutomatedStyleProp('paleTextColor', 'var(--text-primary)')};
    outline: none;
  }

  ${mediaQueries.tablet} {
    padding: var(--space-300);
    top: var(--space-0);
    right: var(--space-0);
  }
`

export const TopContentContainer = styled.div<{
  $withoutPadding: Props['withoutPadding']
}>`
  margin: var(--space-n600) var(--space-n800) var(--space-600);

  ${({ $withoutPadding }) =>
    $withoutPadding &&
    css`
      margin: var(--space-0) var(--space-0) var(--space-600);
    `};
`

export const TitleContainer = styled.div`
  margin-bottom: var(--space-300);

  word-break: break-word;
  color: ${getAutomatedStyleProp('colorOnBackground', 'var(--text-primary)')};

  ${mediaQueries.tablet} {
    margin-bottom: var(--space-300);
  }
`

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  z-index: 10;
  bottom: var(--space-n600);
  margin: var(--space-300) var(--space-n500) var(--space-n600);
  padding: var(--space-300) var(--space-500) var(--space-500);
  background: ${getAutomatedStyleProp('colorBackground', 'var(--bg-primary)')};

  ${mediaQueries.tablet} {
    bottom: var(--space-n600);
    margin: var(--space-n600);
    margin-top: var(--space-400);
    padding: var(--space-600);
    z-index: 2;
    flex-direction: row;
  }
`

export const FooterContent = styled.div`
  display: flex;
  padding: var(--space-400) var(--space-0);
  justify-content: center;
  width: 100%;

  ${mediaQueries.tablet} {
    justify-content: flex-start;
    align-items: center;
    padding: var(--space-0);
  }
`

export const ButtonsContent = styled.div`
  flex: 1;
`

export const ChildrenContainer = styled.div<{
  $disableScrolling: NonNullable<Props['disableScrolling']>
}>`
  flex: 1;

  flex-direction: column;
  display: flex;

  text-underline-offset: 0.15em;
  text-decoration-skip-ink: none;

  p + p {
    margin-top: var(--space-400);
  }

  ${({ $disableScrolling }) =>
    $disableScrolling &&
    css`
      max-height: 100%;
      min-height: 0;
    `};
`
